/**
 * Botanical Effects Section
 * Handles parallax scrolling and cursor-reactive animations for decorative flowers
 */

(function() {
  'use strict';

  class BotanicalEffects {
    constructor(section) {
      this.section = section;
      this.flowers = section.querySelectorAll('.botanical-effects__flower');
      this.effectMode = section.dataset.effectMode || 'parallax';
      this.intensity = parseInt(section.dataset.intensity, 10) || 5;
      
      // Normalized intensity multiplier (1-10 scale to 0.2-2 multiplier)
      this.intensityMultiplier = this.intensity / 5;
      
      // State
      this.scrollY = window.scrollY;
      this.mouseX = 0;
      this.mouseY = 0;
      this.sectionRect = null;
      this.isInView = false;
      this.rafId = null;
      
      // Bind methods
      this.handleScroll = this.handleScroll.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.update = this.update.bind(this);
      
      this.init();
    }

    init() {
      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this.calculateBounds();
      this.setupEventListeners();
      this.startAnimationLoop();
    }

    calculateBounds() {
      this.sectionRect = this.section.getBoundingClientRect();
      this.sectionTop = this.section.offsetTop;
      this.sectionHeight = this.section.offsetHeight;
    }

    setupEventListeners() {
      // Scroll events for parallax
      if (this.effectMode === 'parallax' || this.effectMode === 'hybrid') {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
      }

      // Mouse events for cursor reactive
      if (this.effectMode === 'cursor' || this.effectMode === 'hybrid') {
        this.section.addEventListener('mousemove', this.handleMouseMove, { passive: true });
        this.section.addEventListener('mouseleave', () => {
          this.mouseX = 0.5;
          this.mouseY = 0.5;
        });
      }

      // Resize handler
      window.addEventListener('resize', this.handleResize, { passive: true });

      // Intersection observer for performance
      this.setupIntersectionObserver();
    }

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '100px',
        threshold: 0
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isInView = entry.isIntersecting;
        });
      }, options);

      this.observer.observe(this.section);
    }

    handleScroll() {
      this.scrollY = window.scrollY;
    }

    handleMouseMove(e) {
      const rect = this.section.getBoundingClientRect();
      this.mouseX = (e.clientX - rect.left) / rect.width;
      this.mouseY = (e.clientY - rect.top) / rect.height;
    }

    handleResize() {
      this.calculateBounds();
    }

    startAnimationLoop() {
      this.update();
    }

    update() {
      if (this.isInView) {
        this.flowers.forEach(flower => this.updateFlower(flower));
      }
      
      this.rafId = requestAnimationFrame(this.update);
    }

    updateFlower(flower) {
      const depth = parseInt(flower.dataset.depth, 10) || 3;
      const parallaxSpeed = parseInt(flower.dataset.parallaxSpeed, 10) || 30;
      const baseRotation = parseFloat(flower.style.getPropertyValue('--flower-rotation')) || 0;
      
      let translateX = 0;
      let translateY = 0;
      let rotateX = 0;
      let rotateY = 0;
      let additionalRotation = 0;

      // Parallax effect
      if (this.effectMode === 'parallax' || this.effectMode === 'hybrid') {
        const scrollProgress = (this.scrollY - this.sectionTop + window.innerHeight) / (this.sectionHeight + window.innerHeight);
        const parallaxOffset = (scrollProgress - 0.5) * parallaxSpeed * this.intensityMultiplier;
        
        // Depth affects parallax amount (deeper = slower)
        const depthMultiplier = depth / 3;
        translateY = parallaxOffset * depthMultiplier;
      }

      // Cursor reactive effect
      if (this.effectMode === 'cursor' || this.effectMode === 'hybrid') {
        // Calculate angle from flower to cursor
        const flowerX = parseFloat(flower.style.getPropertyValue('--flower-x')) / 100 || 0.5;
        const flowerY = parseFloat(flower.style.getPropertyValue('--flower-y')) / 100 || 0.5;
        
        const deltaX = this.mouseX - flowerX;
        const deltaY = this.mouseY - flowerY;
        
        // Max tilt of 15 degrees, scaled by intensity
        const maxTilt = 15 * this.intensityMultiplier;
        
        // Flowers closer to foreground (higher depth) react more
        const depthReactivity = depth / 5;
        
        rotateY = deltaX * maxTilt * depthReactivity;
        rotateX = -deltaY * maxTilt * depthReactivity;
        
        // Subtle rotation toward cursor
        additionalRotation = deltaX * 5 * depthReactivity * this.intensityMultiplier;
      }

      // Apply transforms
      const transform = `
        translate(-50%, -50%)
        translate3d(${translateX}px, ${translateY}px, 0)
        rotate(${baseRotation + additionalRotation}deg)
        rotateX(${rotateX}deg)
        rotateY(${rotateY}deg)
      `;

      flower.style.transform = transform;
    }

    destroy() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  // Initialize all botanical sections
  function initBotanicalSections() {
    const sections = document.querySelectorAll('.botanical-effects');
    
    sections.forEach(section => {
      // Skip if static mode
      if (section.dataset.effectMode === 'static') return;
      
      // Skip if already initialized
      if (section.botanicalEffects) return;
      
      section.botanicalEffects = new BotanicalEffects(section);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBotanicalSections);
  } else {
    initBotanicalSections();
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.botanical-effects');
    if (section && section.dataset.effectMode !== 'static') {
      section.botanicalEffects = new BotanicalEffects(section);
    }
  });

  // Cleanup on section unload
  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.botanical-effects');
    if (section && section.botanicalEffects) {
      section.botanicalEffects.destroy();
    }
  });

})();
